var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useCallback, useMemo, useRef, useState, } from 'react';
import { ARROW_DOWN_KEY, ARROW_LEFT_KEY, ARROW_RIGHT_KEY, ARROW_UP_KEY, END_KEY, HOME_KEY, SPACE_KEY, } from '#constants';
import { LISTBOX_OTHER_OPTION_ID } from './ListBox.constants';
import { deriveOtherFieldValue } from './ListBox.services';
export const useListBox = ({ disabled = false, isSingleSelect = true, onChange, options, value = [], }) => {
    _react_refresh_temp_1();
    const optionRefs = useRef([]);
    const [internalValue, setInternalValue] = useState(value);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const optionIds = useMemo(() => options.map((opt) => opt.id), [options]);
    const selectedOptionIds = useMemo(() => internalValue.filter((v) => typeof v === 'string' && optionIds.includes(v)), [internalValue, optionIds]);
    const getTabIndex = useCallback((optionId, index) => {
        let tabIndex;
        if (focusedIndex >= 0) {
            tabIndex = index === focusedIndex ? 0 : -1;
            return tabIndex;
        }
        // A value that is not an option id is the Other option's free text
        const firstSelectedId = internalValue
            .map((selectedValue) => optionIds.includes(selectedValue)
            ? selectedValue
            : LISTBOX_OTHER_OPTION_ID)
            .find((selectedId) => optionIds.includes(selectedId));
        if (firstSelectedId !== undefined) {
            tabIndex = optionId === firstSelectedId ? 0 : -1;
            return tabIndex;
        }
        const firstEnabledIndex = options.findIndex((opt) => !opt.disabled);
        tabIndex = index === firstEnabledIndex ? 0 : -1;
        return tabIndex;
    }, [focusedIndex, internalValue, optionIds, options]);
    const getCurrentFocusedIndex = useCallback(() => {
        const activeElement = document.activeElement;
        return optionRefs.current.findIndex((ref) => ref === activeElement);
    }, []);
    const toggleOption = (optionId) => {
        if (disabled) {
            return;
        }
        const isSelected = selectedOptionIds.includes(optionId);
        const otherFieldValue = deriveOtherFieldValue({
            listBoxValue: internalValue,
            options,
        });
        let newValues;
        // handle other option
        if (optionId === LISTBOX_OTHER_OPTION_ID) {
            if (isSingleSelect) {
                newValues =
                    otherFieldValue || otherFieldValue === '' ? [] : [''];
            }
            else {
                newValues = [
                    ...selectedOptionIds,
                    ...(otherFieldValue || otherFieldValue === '' ? [] : ['']),
                ];
            }
        }
        else {
            if (isSingleSelect) {
                newValues = isSelected ? [] : [optionId];
            }
            else {
                newValues = isSelected
                    ? [
                        ...internalValue.filter((value) => value !== optionId ||
                            (!selectedOptionIds.includes(value) &&
                                value !== otherFieldValue)),
                    ]
                    : [...internalValue, optionId];
            }
        }
        if (onChange) {
            onChange(newValues);
        }
        setInternalValue(newValues);
    };
    const handleOtherFieldChange = (event) => {
        const otherFieldValue = event.target.value;
        const newInternalValue = [
            ...internalValue.filter((v) => optionIds.includes(v)),
            otherFieldValue,
        ];
        if (onChange) {
            onChange(newInternalValue);
        }
        setInternalValue(newInternalValue);
    };
    const handleKeyDown = (event) => {
        if (disabled) {
            return;
        }
        const { key } = event;
        const optionsLength = options.length;
        const currentFocusedIndex = getCurrentFocusedIndex();
        if (currentFocusedIndex === -1) {
            const firstEnabledIndex = options.findIndex((opt) => !opt.disabled);
            if (firstEnabledIndex >= 0) {
                optionRefs.current[firstEnabledIndex]?.focus();
                setFocusedIndex(firstEnabledIndex);
            }
            return;
        }
        switch (key) {
            case ARROW_UP_KEY: {
                event.preventDefault();
                const newIndex = currentFocusedIndex - 1;
                if (newIndex >= 0) {
                    optionRefs.current[newIndex]?.focus();
                    setFocusedIndex(newIndex);
                }
                break;
            }
            case ARROW_DOWN_KEY: {
                event.preventDefault();
                const newIndex = currentFocusedIndex + 1;
                if (newIndex < optionsLength) {
                    optionRefs.current[newIndex]?.focus();
                    setFocusedIndex(newIndex);
                }
                break;
            }
            case ARROW_LEFT_KEY: {
                event.preventDefault();
                const newIndex = currentFocusedIndex - 1;
                const targetIndex = newIndex < 0 ? optionsLength - 1 : newIndex;
                optionRefs.current[targetIndex]?.focus();
                setFocusedIndex(targetIndex);
                break;
            }
            case ARROW_RIGHT_KEY: {
                event.preventDefault();
                const newIndex = currentFocusedIndex + 1;
                const targetIndex = newIndex >= optionsLength ? 0 : newIndex;
                optionRefs.current[targetIndex]?.focus();
                setFocusedIndex(targetIndex);
                break;
            }
            case HOME_KEY:
                event.preventDefault();
                optionRefs.current[0]?.focus();
                setFocusedIndex(0);
                break;
            case END_KEY:
                event.preventDefault();
                optionRefs.current[optionsLength - 1]?.focus();
                setFocusedIndex(optionsLength - 1);
                break;
            case SPACE_KEY: {
                event.preventDefault();
                const focusedOption = options[currentFocusedIndex];
                if (focusedOption && !focusedOption.disabled) {
                    toggleOption(focusedOption.id);
                }
                break;
            }
            default:
                break;
        }
    };
    return {
        handleKeyDown,
        handleOtherFieldChange,
        getTabIndex,
        optionRefs,
        toggleOption,
        listBoxValue: internalValue,
    };
};
_react_refresh_temp_1(useListBox, "8zh2usEpwKM765TJZEPfmEI7rso=");
